import { useState, useRef, useEffect } from "react";
import { themeI18n } from '../utils/i18n_helper';
import { addToCart, updateRecentlyViewed } from '../utils/cart';
import { multilingual_url } from '../utils/multilingual-url';
import { Price } from './Price';
import { Badges } from './Badges';

import '../../styles/components/product-card.scss';

import Icons from "./Icons";

const vendorMetaobjects = JSON.parse(document.querySelector('[data-vendor-metaobjects]').innerHTML).vendors;
const promotionMetaobjects = JSON.parse(document.querySelector('[data-promotions-metaobjects]').innerHTML).promotions;

export const ProductCard = ({ product, sectionSettings, collectionId, searchTerm, position, recommendationsSource, isAboveFold = false }) => {
  const [selectedVariant, setSelectedVariant] = useState(product.selected_or_first_available_variant || product.variants.find((variant) => variant.available));
  const [submitLoading, setSubmitLoading] = useState(false);
  const [variantChanged, setVariantChanged] = useState(false);
  const [hoveredVariant, setHoveredVariant] = useState(null);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const buttonIsQuickView = product.variants.length > 1;

  const vendorHandle = product.vendor.replace(/\s+/g, '-').replace('.', '').toLowerCase();
  const vendorBrand = vendorMetaobjects.find((vendor) => vendor.handle === vendorHandle);

  const lastPromotion = product.metafields?.content?.promotions ? product.metafields?.content?.promotions[product.metafields?.content?.promotions.length - 1] : null;

  const promotionMetaobject = promotionMetaobjects.find((promotion) => {
    if (lastPromotion && typeof lastPromotion === 'string') {
      return lastPromotion.includes(promotion.id);
    } else if (lastPromotion && typeof lastPromotion === 'object') {
      return lastPromotion.id === promotion.id;
    }
  });

  // Find the colour object for the hovered variant to get its image
  const hoveredColour = hoveredVariant ? product.colours?.find(colour => 
    (colour.variant?.id || colour.variant) === hoveredVariant.id
  ) : null;
  
  // Use hovered variant image if hovering, otherwise use selected variant image, otherwise use product featured image
  const activeImage = hoveredColour?.image || hoveredVariant?.featured_image
    ? (hoveredColour?.image || hoveredVariant.featured_image)
    : (variantChanged && selectedVariant?.featured_image ? selectedVariant.featured_image : product.featured_image);
  const productUrl = variantChanged ? multilingual_url(selectedVariant.url) : multilingual_url(product.url);

  const widthsArray = sectionSettings?.widths.split(', ');
  let imageWidths = '';

  widthsArray?.forEach((width, index) => {
    imageWidths += `${product.featured_image}&width=${width} ${width}w`

    if (index < sectionSettings.widths.length - 1) {
      imageWidths += ', ';
    }
  });

  const onSubmit = async (e) => {
    e.preventDefault();

    if (buttonIsQuickView) {
      openQuickView();
    } else {
      setSubmitLoading(true);
      await addToCart(selectedVariant.id, 1);
      setSubmitLoading(false);
    }
  }

  const openQuickView = () => {
    updateRecentlyViewed(product.handle);

    const drawerEvent = new CustomEvent('open-product-drawer', {
      detail: {
        product: {
          title: product.title,
          vendor: product.vendor,
          handle: product.handle,
          url: multilingual_url(`/products/${product.handle}`),
        },
        selectedVariantId: selectedVariant.id
      }
    });

    document.dispatchEvent(drawerEvent);
  }

  const onHrefClick = (event) => {
    event.preventDefault();

    if (collectionId) {
      window.FastSimonSDK.event({
        eventName: window.FastSimonEventName.CollectionProductClicked,
        data: {
          collectionID: collectionId, // (Required)
          productID: product.id, // (Required)
          position: position, // counted from 1
          query: undefined
        }
      });
    } else if (searchTerm) {
      window.FastSimonSDK.event({
        eventName: window.FastSimonEventName.SerpProductClicked,
        data: {
          query: searchTerm, // (Required)
          productID: product.id, // (Required)
          position: position, // counted from 1
        }
      });
    } else if (recommendationsSource) {
      window.FastSimonSDK.event({
        eventName: window.FastSimonEventName.RecommendationProductClicked,
        data: {
          productID: product.id, // the id of the clicked product (Required)
          position: position, // the position of the product in the widget / popup (counted from 1)
          sourceProductID: recommendationsSource // source product ID (example product page product ID)
        }
      });
    } else {
      window.FastSimonSDK.event({
        eventName: window.FastSimonEventName.DirectProductClicked,
        data: {
          productID: product.id, // (Required)
        }
      });
    }

    window.location.href = multilingual_url(productUrl);
  }

  return (
    <div
      className={`
        ${product.available ? 'product-card': 'product-card product-card--sold-out'}
        
        ${sectionSettings?.className ? sectionSettings.className : ''}
        ${product.colours?.length > 1 ? 'has-variants' : ''}
        ${hoveredVariant ? 'product-card--swatch-hovered' : ''}
      `}
      
      >
      <div className="product-card__content-wrapper">
        <form className={`product-card-form`} onSubmit={onSubmit}>
          <div 
            className="product-card__images"
            onMouseEnter={(e) => {
              if (!e.target.closest('.swatches__swatch')) {
                setHoveredVariant(null);
              }
            }}
          >
            <div className="product-card__vendor-badges">
              {product.vendor && (
                <>
                  {vendorBrand?.logo ? (
                    <div className="product-card__vendor-image" dangerouslySetInnerHTML={{__html: vendorBrand.logo}}/>
                  ):(
                    <div className="product-card__vendor-text h5 natural" test={vendorHandle}>
                      {product.vendor}
                    </div>
                  )}
                </>
              )}
            </div>

            <Badges promotionBadge={promotionMetaobject} product={product} className="product-card__product-badges" />

            <a href={multilingual_url(productUrl)} onClick={(event)=> onHrefClick(event)} className="product-card__image-wrapper active" aria-label={product.title}>
              {activeImage ? (
                <>
                  <img
                    src={activeImage + "&width=500"}
                    alt={product.title}
                    className="product-card__image"
                    loading={isAboveFold ? "eager" : (sectionSettings?.loading || "lazy")}
                    fetchpriority={isAboveFold ? "high" : (sectionSettings?.fetchpriority || "auto")}
                    sizes={sectionSettings?.sizes}
                    srcSet={imageWidths}
                  />
                </>
              )
              :
                <Icons size="small" icon="placeholder" />
              }
            </a>

            {product.images.length > 1 && !hoveredVariant && (
              <div className="product-card__image-wrapper hover-image hide-desktop-down">
                <img
                  src={product.images[1] + "&width=500"}
                  alt={product.title}
                  className="product-card__hover-image"
                  loading="lazy"
                  fetchpriority="auto"
                  sizes={sectionSettings?.sizes}
                  srcSet={imageWidths}
                />
              </div>
            )}
          </div>

          <div className="product-card__info-wrapper">
            <div className="product-card__info">
              <div className="product-card__info-main">
                <div className="product-card__title-price-wrapper">
                  <h2 className="p product-card__title single-line-text" >
                    <a href={multilingual_url(productUrl)} onClick={(event)=> onHrefClick(event)} title={ product.title }>
                      {product.title}
                    </a>
                  </h2>

                  <Price product={product} selectedVariant={variantChanged ? selectedVariant : null} />
                </div>

                {product.metafields.content.short_description && (
                  <div className="product-card__short-description text-small">
                    {product.metafields.content.short_description}
                  </div>
                )}
              </div>

              <div className="product-card__swatches-mobile-atc">
                {product.colours?.length > 1 && (
                  <fieldset className="product-card__swatches swatches">
                    {product.colours.map((colour, index) => {
                      // Use colour.variant directly if it's the full variant object, otherwise find it
                      const colourVariant = colour.variant || product.variants.find((variant) => variant.id === colour.variant?.id);

                      if (index > 2) {
                        return null;
                      }

                      return (
                        <button
                          key={index}
                          className={`swatches__swatch ${index > 1 ? 'hide-desktop-down': ''} ${selectedVariant?.id === colourVariant?.id ? 'is-active' : ''}`}
                          title={colour.colour}
                          type="button"
                          onMouseEnter={() => {
                            if (hoverTimeoutRef.current) {
                              clearTimeout(hoverTimeoutRef.current);
                              hoverTimeoutRef.current = null;
                            }
                            const variantImage = colourVariant?.featured_image || colour?.image;
                            if (variantImage && colourVariant) {
                              setHoveredVariant(colourVariant);
                            }
                          }}
                          onMouseLeave={() => {
                            hoverTimeoutRef.current = setTimeout(() => {
                              setHoveredVariant(null);
                            }, 100);
                          }}
                          onClick={() => {
                            if (hoverTimeoutRef.current) {
                              clearTimeout(hoverTimeoutRef.current);
                              hoverTimeoutRef.current = null;
                            }
                            setSelectedVariant(colourVariant);
                            setVariantChanged(true);
                            setHoveredVariant(null);
                          }}
                        >
                          <div className="swatches__swatch-inner">
                            <div className="responsive-image__wrapper">
                              {colour?.image ? (
                                <img
                                  src={colour.image + "&width=30"}
                                  alt={colour.colour}
                                  className="swatches__swatch-image"
                                />
                              ):(
                                <>
                                  {colour.colour}
                                </>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}

                    {product.colours.length > 2 && (
                      <button
                        className={`swatches__more text-small ${product.colours.length == 3 ? 'hide-desktop-up' : ''}`}
                        type="button"
                        onClick={openQuickView}
                      >
                        +
                        <span className="hide-desktop-down">{product.colours.length - 3}</span>
                        <span className="hide-desktop-up">{product.colours.length - 2}</span>
                      </button>
                    )}
                  </fieldset>
                )}

                <button className="product-card__mobile-atc hide-desktop"
                  type="submit"
                  title={themeI18n('products.add_to_cart')}
                >
                  <Icons icon="add-to-cart" size="small" />
                </button>
              </div>

              <button
                className={`button button--primary product-card__desktop-atc hide-mobile ${submitLoading ? 'loading' : ''}`}
                disabled={!selectedVariant.available}
                type="submit"
              >
                <span className="button__content">
                  <span className="button__icon">
                    <Icons icon="cart" />
                  </span>

                  <span className="button__text">
                    {selectedVariant.available ? themeI18n('products.add_to_cart') : themeI18n('products.sold_out')}
                  </span>
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
